import { supabase } from '../../../lib/supabase'
import { COLOR_WORDS } from '../data/colors'

export interface AiSubjectArgs {
  personaName: string
  categoryPrompt: string   // e.g. "folktale" or "a feeling"
  model?: string
}

/**
 * Ask the edge function to generate a specific subject from a category
 * prompt. Haiku is the primary source — its outputs are imaginative and
 * persona-aware. But Haiku occasionally fails (bad key, transient error,
 * prompt-echo, missing API key in dev) and the game can't survive a
 * non-specific subject — re-printing "clothing item" twice would break
 * the colour pick because there's no specific thing to colour. So when
 * Haiku doesn't deliver we drop into a hand-curated dictionary that
 * maps each prompt keyword to a list of real, picture-able subjects.
 *
 * Fallback choice is deterministic per (persona, prompt). Same AI re-
 * prompted lands on the same word; different AIs hitting the same
 * prompt usually spread across the list.
 */
export async function requestAiSubject(args: AiSubjectArgs): Promise<string> {
  try {
    const { data, error } = await supabase.functions.invoke('generate-colorful-subject', {
      body: {
        persona_name: args.personaName,
        category_prompt: args.categoryPrompt,
        model: args.model,
      },
    })
    if (error) {
      console.warn('[colorful-ai] subject edge-fn error:', error)
      return pickFallbackSubject(args.personaName, args.categoryPrompt)
    }

    const subject = typeof data?.subject === 'string' ? data.subject.trim() : ''
    const source = (data?.source as string | undefined) ?? 'unknown'
    const reason = (data?.reason as string | undefined) ?? ''
    console.log('[colorful-ai] subject prompt=%o persona=%o → subject=%o source=%s%s',
      args.categoryPrompt, args.personaName, subject, source, reason ? ` reason=${reason}` : '')

    if (!subject
        || containsColorWord(subject)
        || matchesPrompt(subject, args.categoryPrompt)) {
      console.log('[colorful-ai] subject rejected, using fallback dictionary')
      return pickFallbackSubject(args.personaName, args.categoryPrompt)
    }
    return subject
  } catch (err) {
    console.warn('[colorful-ai] subject call threw:', err)
    return pickFallbackSubject(args.personaName, args.categoryPrompt)
  }
}

function containsColorWord(s: string): boolean {
  const lower = s.toLowerCase()
  return COLOR_WORDS.some(w => new RegExp(`\\b${w}\\b`).test(lower))
}

function matchesPrompt(subject: string, prompt: string): boolean {
  const a = normalise(subject)
  const b = normalise(prompt)
  return a === b
}

function normalise(s: string): string {
  return s.trim().toLowerCase().replace(/^(a |an |the )/, '')
}

// ── Fallback dictionary ────────────────────────────────────────────────
//
// Maps a substring keyword (matched against the lowercased prompt) to a
// list of specific example subjects. Keys are tried in order, so list
// multi-word/specific keys before single-word generics. Every value is
// a real, picture-able noun so the colour-picking step makes sense.
//
// Covers every prompt across the 60 category cards (240 prompts total),
// including the deck's typos ("colrful herb", "sceintific discovery").

const FALLBACK_SUBJECTS: ReadonlyArray<readonly [string, readonly string[]]> = [
  // ── Multi-word, specific keys (must come before single-word generics) ──

  // Animals
  ['furry animal',     ['rabbit', 'fox', 'bear', 'sheep']],
  ['smooth animal',    ['dolphin', 'whale', 'snake', 'salamander']],
  ['silly animal',     ['platypus', 'sloth', 'penguin', 'capybara']],
  ['extinct animal',   ['dodo', 'mammoth', 'sabre-tooth tiger', 'velociraptor']],
  ['imaginary animal', ['unicorn', 'griffin', 'phoenix', 'chimera']],
  ['imaginary creature', ['kraken', 'goblin', 'sphinx', 'centaur']],

  // Place
  ['imaginary place',  ['Narnia', 'Atlantis', 'Hogwarts', 'Wonderland']],
  ['crowded place',    ['Times Square', 'Shibuya Crossing', 'subway platform', 'concert pit']],
  ['old place',        ['Colosseum', 'Stonehenge', 'Pompeii', 'Petra']],
  ['new place',        ['Burj Khalifa', 'High Line', 'The Shard', 'Dubai Mall']],
  ['amusement park',   ['Disneyland', 'Universal Studios', 'Six Flags', 'Tivoli Gardens']],
  ['travel brand',     ['Hilton', 'Marriott', 'Airbnb', 'Expedia']],
  ['car maker',        ['Toyota', 'Ferrari', 'Volvo', 'Honda']],
  ['rock type',        ['granite', 'marble', 'obsidian', 'sandstone']],

  // Time
  ['period in history',['Renaissance', 'Stone Age', 'Industrial Revolution', 'Roman Empire']],
  ['2010',             ['Tinder', 'Instagram', 'AirPods', 'TikTok']],
  ['1990',             ['Tamagotchi', 'Furby', 'Walkman', 'pager']],
  ['1980',             ["Rubik's cube", 'boombox', 'cassette tape', 'leg warmers']],
  ['1950',             ['poodle skirt', 'jukebox', 'Cadillac', 'milkshake']],
  ['natural phenomenon', ['aurora', 'eclipse', 'tornado', 'tsunami']],

  // Food
  ['fast food',        ['Big Mac', 'taco', 'burrito', 'fried chicken']],
  ['local food',       ['cheeseburger', 'apple pie', 'BBQ ribs', 'hot dog']],
  ['american food',    ['cheeseburger', 'apple pie', 'BBQ ribs', 'hot dog']],
  ['chinese food',     ['kung pao chicken', 'dumplings', 'fried rice', 'Peking duck']],
  ['japanese food',    ['sushi', 'ramen', 'tempura', 'okonomiyaki']],
  ['sweets shop',      ['candy store', 'bakery', 'donut shop', 'ice cream parlor']],
  ['cafe/bar',         ['speakeasy', 'sports bar', 'tiki bar', 'wine bar']],

  // Senses
  ['a feeling',        ['nostalgia', 'regret', 'serenity', 'envy']],
  ['a texture',        ['silk', 'sandpaper', 'velvet', 'slime']],
  ['a taste',          ['umami', 'tartness', 'savory', 'metallic']],
  ['a smell',          ['petrichor', 'vanilla', 'gasoline', 'cinnamon']],

  // Sports
  ['ball sport',       ['basketball', 'soccer', 'volleyball', 'tennis']],
  ['famous sport',     ['football', 'cricket', 'rugby', 'soccer']],
  ['uncommon sport',   ['curling', 'underwater hockey', 'sepak takraw', 'kabaddi']],
  ['olympic sport',    ['gymnastics', 'archery', 'fencing', 'judo']],

  // Music + entertainment
  ['genre of music',   ['jazz', 'reggae', 'punk', 'classical']],
  ['musical instrument', ['violin', 'saxophone', 'drums', 'harp']],
  ['famous musician',  ['Beyoncé', 'Bowie', 'Mozart', 'Hendrix']],
  ['monster movie',    ['Godzilla', 'King Kong', 'Frankenstein', 'Alien']],
  ['music album',      ['Thriller', 'Abbey Road', 'Lemonade', 'Rumours']],
  ['tv show',          ['Friends', 'Breaking Bad', 'Seinfeld', 'Game of Thrones']],
  ['tv star',          ['Oprah', 'Jimmy Fallon', 'Ellen', "Conan O'Brien"]],
  ['pop star',         ['Taylor Swift', 'Ariana Grande', 'Justin Bieber', 'Bruno Mars']],

  // Names / words
  ['pet name',         ['Buddy', 'Whiskers', 'Rex', 'Mittens']],
  ['old name',         ['Mildred', 'Eugene', 'Edna', 'Walter']],
  ['common name',      ['John', 'Mary', 'David', 'Sarah']],
  ['any name',         ['Alex', 'Jamie', 'Taylor', 'Morgan']],
  ['four letter word', ['ship', 'tree', 'love', 'wind']],
  ['starts with "k"',  ['kettle', 'kite', 'koala', 'kayak']],
  ['starts with "h"',  ['hammer', 'helmet', 'horse', 'harp']],
  ['starts with "y"',  ['yacht', 'yodel', 'yarn', 'yam']],
  ['letter or a number', ['letter A', 'number 7', 'letter Z', 'number 42']],
  ['a language',       ['Mandarin', 'Spanish', 'Swahili', 'French']],
  ['a sound',          ['thunderclap', 'whistle', 'meow', 'sneeze']],
  ['a word',           ['serendipity', 'umbrella', 'whisper', 'cathedral']],

  // Jobs / shopping
  ['job title',        ['surgeon', 'pilot', 'electrician', 'chef']],
  ['a ceo',            ['Jeff Bezos', 'Tim Cook', 'Mary Barra', 'Elon Musk']],
  ['clothing item',    ['hoodie', 'denim jacket', 'sneakers', 'baseball cap']],
  ['item of clothing', ['hoodie', 'denim jacket', 'sneakers', 'baseball cap']],
  ['shoe brand',       ['Nike', 'Adidas', 'Converse', 'Vans']],
  ['shirt brand',      ['Hanes', 'Uniqlo', 'Lacoste', 'Gap']],
  ['fabric type',      ['denim', 'silk', 'wool', 'linen']],
  ['dress style',      ['kimono', 'sari', 'tuxedo', 'gown']],
  ['type of uniform',  ['nurse uniform', 'military uniform', 'school uniform', 'chef uniform']],
  ['currency bill',    ['dollar', 'euro', 'yen', 'pound']],

  // School / tools
  ['a school',         ['Harvard', 'MIT', 'Oxford', 'Stanford']],
  ['classroom object', ['chalkboard', 'pencil', 'globe', 'desk']],
  ['technology item',  ['laptop', 'smartphone', 'headphones', 'drone']],
  ['wood working',     ['lathe', 'chisel', 'hand plane', 'saw']],
  ['any tool',         ['hammer', 'drill', 'wrench', 'screwdriver']],
  ['chef tool',        ['whisk', 'spatula', 'cleaver', 'rolling pin']],
  ['farm tool',        ['pitchfork', 'sickle', 'plow', 'rake']],
  ['art tool',         ['paintbrush', 'easel', 'palette', 'sketchbook']],

  // Objects
  ['common item',      ['toothbrush', 'umbrella', 'mug', 'pillow']],
  ['fictional item',   ['lightsaber', 'invisibility cloak', 'one ring-3', 'time turner']],
  ['rare item',        ['Fabergé egg', 'meteorite', 'unicorn horn', 'moon rock']],
  ['famous object',    ['Mona Lisa', 'Hope Diamond', 'Liberty Bell', 'Eiffel Tower']],
  ['invisible thing',  ['gravity', 'wifi', 'wind', 'magnetism']],
  ['skin care',        ['moisturizer', 'sunscreen', 'face mask', 'cleanser']],

  // Characters
  ['fairy tale character', ['Cinderella', 'Sleeping Beauty', 'Goldilocks', 'Rapunzel']],
  ['film character',   ['James Bond', 'Indiana Jones', 'Forrest Gump', 'The Joker']],
  ['animated character', ['Mickey Mouse', 'Pikachu', 'Bart Simpson', 'SpongeBob']],
  ['cultural character', ['Uncle Sam', 'John Henry', 'Paul Bunyan', 'Anansi']],

  // Folklore
  ['a ritual',         ['baptism', 'tea ceremony', 'rain dance', 'handshake']],

  // Films / books
  ['a film',           ['Casablanca', 'The Godfather', 'Inception', 'Parasite']],
  ['a novel',          ['1984', 'Pride and Prejudice', 'Dune', 'Beloved']],
  ['a story',          ['Cinderella', "Aesop's Fables", 'Romeo and Juliet', 'The Tortoise and the Hare']],
  ['a music album',    ['Thriller', 'Abbey Road', 'Lemonade', 'Rumours']],

  // Misc
  ['a praise',         ['bravo', 'wonderful', 'magnificent', 'brilliant']],
  ['a scientist',      ['Einstein', 'Curie', 'Newton', 'Darwin']],

  // Preferences
  ['something you fear',         ['spiders', 'heights', 'dentists', 'thunder']],
  ['something you enjoy',        ['ice cream', 'a hot bath', 'reading', 'dancing']],
  ['something you give away',    ['old clothes', 'compliments', 'leftovers', 'advice']],
  ['something you are',          ['curious', 'tired', 'human', 'an artist']],

  // Actions
  ['action done in the morning', ['brushing teeth', 'making coffee', 'stretching', 'reading the news']],
  ['action done before bed',     ['flossing', 'praying', 'setting an alarm', 'meditating']],
  ['action done in public',      ['waving hello', 'checking the time', 'tipping a hat', 'paying for food']],
  ['action done in private',     ['singing in the shower', 'crying', 'journaling', 'dancing alone']],

  // Imagination
  ['two words mixed together',   ['glamping', 'spork', 'chillax', 'brunch']],

  // Games
  ['classic game',     ['Monopoly', 'chess', 'checkers', 'Scrabble']],
  ['video game',       ['Mario', 'Tetris', 'Pac-Man', 'Minecraft']],
  ['card game',        ['poker', 'Uno', 'solitaire', 'go fish']],
  ['board game',       ['Catan', 'Risk', 'Clue', 'Sorry']],
  ['abstract game',    ['chess', 'Go', 'checkers', 'mancala']],

  // Science (with deck typo)
  ['scientific discovery', ['penicillin', 'gravity', 'electricity', 'DNA']],
  ['sceintific discovery', ['penicillin', 'gravity', 'electricity', 'DNA']],

  // Candy / snacks
  ['soft candy',       ['marshmallow', 'gummy worm', 'taffy', 'caramel']],
  ['hard candy',       ['Jolly Rancher', 'lollipop', 'lifesaver', 'jawbreaker']],
  ['round candy',      ['gobstopper', 'M&M', 'Skittles', 'jawbreaker']],
  ['long candy',       ['Twizzler', 'licorice rope', 'Pixy Stix', 'Push Pop']],

  // Herbs / scents (with deck typo)
  ['sweet herb',       ['basil', 'mint', 'lavender', 'tarragon']],
  ['strong herb',      ['rosemary', 'sage', 'oregano', 'thyme']],
  ['bitter herb',      ['rue', 'wormwood', 'dandelion', 'arugula']],
  ['colrful herb',     ['saffron', 'turmeric', 'paprika', 'lavender']],
  ['colorful herb',    ['saffron', 'turmeric', 'paprika', 'lavender']],
  ['strong scent',     ['skunk', 'gasoline', 'durian', 'garlic']],
  ['sweet scent',      ['vanilla', 'rose', 'jasmine', 'honeysuckle']],
  ['foul scent',       ['rotten egg', 'sewage', 'mildew', 'mold']],
  ['nice scent',       ['lavender', 'fresh laundry', 'baked bread', 'pine']],

  // Art / architecture
  ['art form',         ['origami', 'mosaic', 'graffiti', 'pottery']],
  ['a building',       ['Empire State', 'Big Ben', 'Sagrada Familia', 'Eiffel Tower']],
  ['architectural style', ['Gothic', 'Art Deco', 'Bauhaus', 'Brutalist']],

  // Design
  ['brand of razor',   ['Gillette', 'Schick', 'BIC', "Harry's"]],
  ['japanese design brand', ['Muji', 'Uniqlo', 'Sony', 'Nintendo']],

  // Culture
  ['cultural item',    ['kimono', 'sombrero', 'didgeridoo', 'fortune cookie']],
  ['cultural celebration', ['Diwali', 'Lunar New Year', 'Holi', 'Hanukkah']],

  // ── Single-word generic keys ────────────────────────────────────────

  // Animals
  ['mammal',    ['elephant', 'tiger', 'rhino', 'hippo']],
  ['bird',      ['robin', 'eagle', 'owl', 'flamingo']],
  ['fish',      ['salmon', 'tuna', 'koi', 'shark']],
  ['insect',    ['butterfly', 'beetle', 'ant', 'dragonfly']],
  ['organism',  ['amoeba', 'fungus', 'algae', 'bacteria']],
  ['animal',    ['elephant', 'tiger', 'panda', 'koala']],

  // Plants
  ['vegetable', ['carrot', 'cucumber', 'broccoli', 'eggplant']],
  ['flower',    ['rose', 'tulip', 'daisy', 'orchid']],
  ['tree',      ['oak', 'maple', 'pine', 'willow']],
  ['plant',     ['fern', 'cactus', 'ivy', 'bamboo']],

  // Time
  ['seasoning', ['paprika', 'cumin', 'rosemary', 'cinnamon']],
  ['season',    ['autumn', 'spring', 'summer', 'winter']],
  ['month',     ['January', 'July', 'October', 'December']],
  ['time',      ['midnight', 'sunrise', 'dusk', 'noon']],

  // Geo
  ['weather',   ['hurricane', 'blizzard', 'rainbow', 'thunderstorm']],
  ['planet',    ['Mars', 'Saturn', 'Jupiter', 'Mercury']],
  ['landscape', ['canyon', 'meadow', 'tundra', 'rainforest']],

  // Place
  ['building',  ['lighthouse', 'pyramid', 'cathedral', 'skyscraper']],
  ['continent', ['Asia', 'Africa', 'Europe', 'Antarctica']],
  ['country',   ['Japan', 'Brazil', 'France', 'Egypt']],
  ['nation',    ['Japan', 'Mexico', 'Sweden', 'Kenya']],
  ['city',      ['Tokyo', 'Paris', 'Cairo', 'Reykjavik']],
  ['airline',   ['Emirates', 'Lufthansa', 'Qantas', 'Delta']],
  ['subway',    ['Tokyo Metro', 'NYC Subway', 'Paris Métro', 'London Underground']],

  // Vehicles
  ['vehicle',   ['scooter', 'tractor', 'tank', 'bicycle']],
  ['car',       ['Mustang', 'Beetle', 'Civic', 'Tesla']],
  ['airplane',  ['Boeing 747', 'Cessna', 'biplane', 'glider']],
  ['boat',      ['kayak', 'gondola', 'dinghy', 'yacht']],

  // Amusement
  ['celebration', ['birthday party', 'graduation', 'wedding', 'baby shower']],
  ['festival',  ['Coachella', 'Mardi Gras', 'Oktoberfest', 'Holi']],
  ['gathering', ['barbecue', 'reunion', 'meetup', 'rally']],
  ['event',     ['parade', 'concert', 'marathon', 'fair']],
  ['show',      ['Friends', 'parade', 'fireworks', 'magic show']],

  // Food
  ['breakfast', ['pancakes', 'omelette', 'oatmeal', 'bagel']],
  ['lunch',     ['sandwich', 'salad', 'soup', 'wrap']],
  ['dinner',    ['lasagna', 'roast chicken', 'sushi', 'curry']],
  ['snack',     ['popcorn', 'pretzel', 'granola bar', 'trail mix']],
  ['restaurant',['Olive Garden', 'IHOP', 'Cheesecake Factory', "Denny's"]],
  ['cafe',      ['Starbucks', 'Costa Coffee', 'espresso bar', 'tea house']],
  ['bar',       ['speakeasy', 'sports bar', 'tiki bar', 'wine bar']],
  ['drink',     ['cola', 'lemonade', 'iced coffee', 'beer']],
  ['flavor',    ['vanilla', 'chocolate', 'mint', 'caramel']],
  ['edible',    ['apple', 'cracker', 'cheese', 'almond']],
  ['food',      ['pizza', 'sushi', 'tacos', 'pasta']],

  // Senses
  ['feeling',   ['nostalgia', 'regret', 'serenity', 'envy']],
  ['emotion',   ['joy', 'rage', 'awe', 'shame']],
  ['texture',   ['silk', 'sandpaper', 'velvet', 'slime']],
  ['taste',     ['umami', 'tartness', 'savory', 'metallic']],
  ['smell',     ['petrichor', 'vanilla', 'gasoline', 'cinnamon']],

  // Celebrity
  ['artist',    ['Banksy', 'Picasso', 'Warhol', 'Frida Kahlo']],
  ['athlete',   ['Serena Williams', 'Lebron James', 'Messi', 'Usain Bolt']],
  ['author',    ['Stephen King', 'Toni Morrison', 'Murakami', 'Tolkien']],
  ['actor',     ['Tom Hanks', 'Meryl Streep', 'Denzel Washington', 'Cate Blanchett']],
  ['singer',    ['Beyoncé', 'Adele', 'Drake', 'Taylor Swift']],
  ['politician',['Obama', 'Merkel', 'Mandela', 'Churchill']],
  ['scientist', ['Einstein', 'Curie', 'Newton', 'Darwin']],
  ['ceo',       ['Jeff Bezos', 'Tim Cook', 'Mary Barra', 'Elon Musk']],
  ['designer',  ['Coco Chanel', 'Dieter Rams', 'Issey Miyake', 'Karl Lagerfeld']],

  // Entertainment
  ['team',      ['Lakers', 'Yankees', 'Real Madrid', 'Manchester United']],
  ['movie',     ['Inception', 'Titanic', 'Jurassic Park', 'Spirited Away']],
  ['album',     ['Thriller', 'Abbey Road', 'Lemonade', 'Rumours']],
  ['band',      ['Radiohead', 'BTS', 'Arctic Monkeys', 'Coldplay']],
  ['series',    ['Breaking Bad', 'The Office', 'Stranger Things', 'Sherlock']],
  ['song',      ['Bohemian Rhapsody', 'Hey Jude', 'Imagine', 'Like a Rolling Stone']],

  // Words
  ['noun',      ['hammer', 'cloud', 'castle', 'lantern']],
  ['verb',      ['sprint', 'whistle', 'tumble', 'gallop']],
  ['adjective', ['fluffy', 'jagged', 'silken', 'crisp']],
  ['praise',    ['bravo', 'wonderful', 'magnificent', 'brilliant']],

  // Jobs / shopping
  ['specialty', ['cardiology', 'dermatology', 'orthodontics', 'astrophysics']],
  ['labor',     ['carpenter', 'mason', 'plumber', 'farmer']],
  ['accessory', ['scarf', 'belt', 'wristwatch', 'earrings']],
  ['fabric',    ['denim', 'silk', 'wool', 'linen']],
  ['cosmetics', ['mascara', 'lipstick', 'foundation', 'blush']],
  ['currency',  ['dollar', 'euro', 'yen', 'pound']],
  ['store',     ['Target', 'IKEA', 'Best Buy', 'Walmart']],
  ['grocer',    ['Whole Foods', "Trader Joe's", 'Aldi', 'Kroger']],
  ['occupation',['surgeon', 'pilot', 'electrician', 'chef']],

  // Tools / containers
  ['stationary',['stapler', 'highlighter', 'paperclip', 'eraser']],
  ['container', ['mason jar', 'thermos', 'lunchbox', 'tupperware']],
  ['plastic',   ['shopping bag', 'lego brick', 'water bottle', 'frisbee']],

  // Materials
  ['liquid',    ['mercury', 'olive oil', 'syrup', 'milk']],
  ['solid',     ['brick', 'iron bar', 'ice cube', 'wood block']],
  ['gas',       ['helium', 'oxygen', 'steam', 'methane']],
  ['material',  ['steel', 'concrete', 'glass', 'rubber']],

  // Descriptors / characteristics
  ['light',     ['feather', 'cotton ball', 'bubble', 'leaf']],
  ['heavy',     ['anvil', 'piano', 'boulder', 'cannonball']],
  ['round',     ['globe', 'basketball', 'cantaloupe', 'donut']],
  ['sharp',     ['needle', 'razor blade', 'knife', 'thorn']],
  ['long',      ['snake', 'broomstick', 'noodle', 'limousine']],
  ['shiny',     ['mirror', 'tinsel', 'diamond', 'chrome bumper']],
  ['stretchy',  ['rubber band', 'bungee cord', 'gummy worm', 'spandex']],
  ['flat',      ['pancake', 'plate', 'pizza', 'doormat']],

  // Sports / subculture
  ['sport',     ['surfing', 'skiing', 'tennis', 'rugby']],
  ['subculture',['goth', 'hippie', 'cosplayer', 'biker']],
  ['taboo',     ['cannibalism', 'incest', 'profanity', 'sacrilege']],

  // Fantasy
  ['monster',   ['Bigfoot', 'kraken', 'minotaur', 'cyclops']],
  ['hero',      ['Superman', 'Hercules', 'Joan of Arc', 'King Arthur']],
  ['villain',   ['Voldemort', 'Darth Vader', 'Maleficent', 'The Joker']],
  ['magical',   ['wizard', 'fairy', 'genie', 'witch']],

  // Folklore
  ['folktale',  ['Cinderella', 'Anansi', 'Paul Bunyan', 'Three Little Pigs']],
  ['myth',      ["Pandora's Box", 'Trojan Horse', 'Atlantis', 'Odyssey']],
  ['conspiracy',['flat earth', 'Roswell', 'Bigfoot', 'Bermuda Triangle']],

  // Experiences
  ['joy',       ['laughter', 'first kiss', 'puppies', 'sunshine']],
  ['nostalgia', ['old photos', 'childhood toys', 'family vacation', 'school dance']],
  ['laughter',  ['stand-up comedy', 'tickle fight', 'dad joke', 'sitcom']],
  ['boredom',   ['Sunday afternoon', 'long meeting', 'waiting room', 'rainy day']],

  // Language
  ['letter',    ['letter A', 'letter Z', 'letter M', 'letter Q']],
  ['number',    ['number 7', 'number 42', 'number 13', 'number 100']],
  ['language',  ['Mandarin', 'Spanish', 'Swahili', 'French']],
  ['sound',     ['thunderclap', 'whistle', 'meow', 'sneeze']],
  ['word',      ['serendipity', 'umbrella', 'whisper', 'cathedral']],

  // School
  ['subject',   ['biology', 'algebra', 'history', 'literature']],

  // Snacks
  ['chips',     ['Doritos', 'Lays', 'Pringles', 'Cheetos']],
  ['cookies',   ['Oreo', 'chocolate chip', 'snickerdoodle', 'biscotti']],
  ['cake',      ['cheesecake', 'tiramisu', 'angel food', 'carrot cake']],

  // Herbs / scents
  ['herb',      ['basil', 'rosemary', 'thyme', 'sage']],
  ['scent',     ['lavender', 'cinnamon', 'gasoline', 'rose']],

  // Art
  ['painting',  ['Starry Night', 'Mona Lisa', 'The Scream', 'Guernica']],
  ['sculpture', ['David', 'Venus de Milo', 'The Thinker', 'Pieta']],
  ['chair',     ['Eames lounge chair', 'Adirondack chair', 'beanbag', 'wingback']],

  // Tastes
  ['citrus',    ['lemon', 'grapefruit', 'mandarin', 'pomelo']],
  ['bitter',    ['coffee', 'dark chocolate', 'kale', 'tonic water']],
  ['herbal',    ['chamomile', 'mint tea', 'lemon balm', 'eucalyptus']],
  ['sour',      ['lemon', 'sauerkraut', 'kimchi', 'sour candy']],

  // Imagination
  ['imaginary', ['dragon', 'fairy', 'unicorn', 'gnome']],

  // Science
  ['discovery', ['penicillin', 'gravity', 'electricity', 'DNA']],
  ['invention', ['light bulb', 'telephone', 'wheel', 'printing press']],
] as const

export function pickFallbackSubject(persona: string, prompt: string): string {
  const lower = prompt.toLowerCase()
  for (const [key, choices] of FALLBACK_SUBJECTS) {
    if (lower.includes(key)) {
      // Extra defence: filter out any value that happens to contain a
      // colour word. None of the curated lists do, but the rule in the
      // physical game is hard — better belt-and-braces than ship a bug.
      const safe = choices.filter(c => !containsColorWord(c))
      const list = safe.length > 0 ? safe : choices
      const idx = hashStringToInt(persona + '|' + prompt) % list.length
      return list[idx]
    }
  }
  // Generic catch-all if no key matched the prompt. Picture-able,
  // colour-neutral nouns so the colour pick still has somewhere to go.
  const generics = ['cathedral', 'lighthouse', 'umbrella', 'lantern', 'piano', 'orchid']
  return generics[hashStringToInt(persona + '|' + prompt) % generics.length]
}

// Tiny FNV-1a-style hash for stable bucketing of (persona, prompt) into
// the fallback list. Doesn't need to be cryptographic — just to spread
// the same persona across different prompts and the same prompt across
// different personas.
function hashStringToInt(s: string): number {
  let h = 2166136261 >>> 0
  for (let i = 0; i < s.length; i++) {
    h = (h ^ s.charCodeAt(i)) >>> 0
    h = Math.imul(h, 16777619) >>> 0
  }
  return h
}
